import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useSchedulers } from "../hooks/useSchedulers";
import { Box, Button, Card, CardContent, Typography, Chip, Divider, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { SchedulerType, IScheduler } from "../../../types/database/Scheduler";
import { AdminSchedulerModal } from "./components/AdminSchedulerModal";
import LinkIcon from '@mui/icons-material/Link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FlagIcon from '@mui/icons-material/Flag';
import MailIcon from '@mui/icons-material/Mail';

const dayOfWeekMap: { [key: number]: string } = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat'
};

export const AdminProjectCalendars = () => {
    const { project, reloadProjectData } = useOutletContext<any>();
    const { schedulers, fetched, error, reloadSchedulers } = useSchedulers(project.tag);
    const [selectedScheduler, setSelectedScheduler] = useState<IScheduler | null>(null);
    const [selectedType, setSelectedType] = useState<SchedulerType | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    if (!fetched) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error loading schedulers: {error}</div>;
    }

    const getSchedulersForType = (type: string) => {
        return schedulers?.filter((s: IScheduler) => s.type === type) || [];
    };

    const handleEditScheduler = (scheduler: IScheduler | null, type: SchedulerType) => {
        setSelectedScheduler(scheduler);
        setSelectedType(type);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedScheduler(null);
        setSelectedType(null);
    };

    const handleModalSubmit = () => {
        reloadSchedulers();
        reloadProjectData();
        handleModalClose();
    };

    const renderAvailabilityWindows = (scheduler: IScheduler) => {
        if (scheduler.provider !== "custom" || !scheduler.availability_windows?.length) return null;

        const groupedWindows = scheduler.availability_windows.reduce((acc: { [key: string]: any[] }, window) => {
            const day = dayOfWeekMap[window.day_of_week];
            if (!acc[day]) acc[day] = [];
            acc[day].push(window);
            return acc;
        }, {});

        return (
            <Grid item xs={12}>
                <Typography variant="subtitle2">Availability Windows</Typography>
                <Box sx={{ mt: 0.5 }}>
                    {Object.entries(groupedWindows).map(([day, windows]) => (
                        <Box key={day} sx={{ mb: 0.5 }}>
                            <Typography variant="body2" component="span" color="text.secondary" sx={{ mr: 1 }}>
                                {day}:
                            </Typography>
                            {windows.map((window, index) => (
                                <Typography key={index} variant="body2" component="span" sx={{ mr: 2 }}>
                                    {window.start_time} - {window.end_time}
                                </Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Grid>
        );
    };

    const renderCalendars = (scheduler: IScheduler) => {
        if (scheduler.provider !== "custom" || !scheduler.calendars?.length) return null;

        return (
            <Grid item xs={12}>
                <Typography variant="subtitle2">Connected Calendars</Typography>
                <Box sx={{ mt: 0.5 }}>
                    {scheduler.calendars.map((calendar, index) => (
                        <Typography key={calendar.id} variant="body2" component="span" sx={{ mr: 2 }}>
                            {calendar.name}
                        </Typography>
                    ))}
                </Box>
            </Grid>
        );
    };

    const renderSchedulerSection = (type: SchedulerType, schedulerProperties: any) => {
        const typeSchedulers = getSchedulersForType(type);
        return (
            <>
                {typeSchedulers.length > 0 ? (
                    typeSchedulers.map((scheduler: IScheduler) => (
                        <Box key={scheduler.id} sx={{ mb: 2 }} className="display-box white">
                            <Box>
                                {/* Title & Status */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: scheduler.scheduling_url && scheduler.scheduling_url !== "" ? 'success.main' : 'error.main',
                                                    mr: 1,
                                                    display: 'inline-block',
                                                }}
                                            />
                                            {scheduler.type.toUpperCase()}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        onClick={() => handleEditScheduler(scheduler, type)}
                                    >
                                        Edit
                                    </Button>
                                </Box>

                                <Box>
                                    <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                                    {schedulerProperties.name}
                                    </Typography>
                                    <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                        {schedulerProperties.subtitle}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 2 }}>
                                    {schedulerProperties.description}
                                </Typography>
                                </Box>

                                {/* Description */}


                                <Divider sx={{ my: 1 }} />

                                {/* Meeting Details */}
                                <Stack>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AccessTimeIcon fontSize="small" />
                                            <Typography variant="body2"><b>Duration:</b> {schedulerProperties.duration} min</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon fontSize="small" />
                                            <Typography variant="body2"><b>Suggested Host:</b> {schedulerProperties.host}</Typography>
                                        </Box>
                                    </Box>
                                </Stack>

                                <Divider sx={{ my: 2 }} />

                                {/* Participants */}
                                <Stack>
                                <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarTodayIcon fontSize="small" />
                                            <Typography variant="body2"><b>Provider:</b> {scheduler.provider === "custom" ? "Custom Calendar" : "Calendly"}</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LinkIcon fontSize="small" />
                                            <Typography variant="body2"><b>URL:</b> <a href={scheduler.scheduling_url} target="_blank" rel="noopener noreferrer">{scheduler.scheduling_url}</a></Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <MailIcon fontSize="small" />
                                            <Typography variant="body2"><b>Internal Attendee:</b> {scheduler.internal_attendee || 'None'}</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <GroupIcon fontSize="small" />
                                            <Typography variant="body2"><b>Attendees:</b> {scheduler.attendees?.map((a: any) => a.email).join(', ') || 'None'}</Typography>
                                        </Box>
                                    </Box>
                                </Stack>

                                {/* Custom Provider Details */}
                                {scheduler.provider === "custom" && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        {renderAvailabilityWindows(scheduler)}
                                        {renderCalendars(scheduler)}
                                    </>
                                )}
                            </Box>
                        </Box>
                    ))
                ) : (
                    <Box sx={{ mb: 2 }} className="display-box white">
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="h6">{schedulerProperties.name}</Typography>
                                <Button
                                    variant="contained"
                                    onClick={() => handleEditScheduler(null, type)}
                                >
                                    Configure
                                </Button>
                            </Box>
                            <Typography color="text.secondary">
                                No scheduler configured for this meeting type.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </>
        );
    };

    // Get all unique scheduler types from the data
    const allSchedulerTypes = Array.from(new Set(schedulers?.map((s: IScheduler) => s.type) || []));

    // Filter out the main types to get other types
    const otherTypes = allSchedulerTypes.filter(type =>
        !Object.values(SchedulerType).includes(type as SchedulerType)
    );

    return (
        <Stack gap={2} sx={{ p: 3 }}>

            {/* Main Types */}
            {renderSchedulerSection(SchedulerType.ONBOARDING, project.effective_scheduler_properties?.[SchedulerType.ONBOARDING])}
            {renderSchedulerSection(SchedulerType.INTRO, project.effective_scheduler_properties?.[SchedulerType.INTRO])}
            {renderSchedulerSection(SchedulerType.DEMO, project.effective_scheduler_properties?.[SchedulerType.DEMO])}
            {renderSchedulerSection(SchedulerType.TRIAL, project.effective_scheduler_properties?.[SchedulerType.TRIAL])}

            {/* Other Types */}
            {otherTypes.length > 0 && (
                <>
                    <h5>Other Calendars</h5>
                    {otherTypes.map(type => (
                        renderSchedulerSection(type, project.effective_scheduler_properties?.[type as keyof typeof project.effective_scheduler_properties])
                    ))}
                </>
            )}

            {isModalOpen && (
                <AdminSchedulerModal
                    open={isModalOpen}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                    scheduler={selectedScheduler}
                    projectTag={project.tag}
                    projectId={project.id}
                    type={selectedType as SchedulerType}
                />
            )}
        </Stack>
    );
};